// WithdrawDetailDrawer — pen frame t0hY8H (抽屉-提现详情)
//
// 通过 GET /gapi/agent/withdraw/{id} 拉单条详情;展示状态机 + 基本信息 + 拒绝原因(若有)。
import { useEffect } from 'react'
import { X } from 'lucide-react'
import { Spinner, formatDate } from '@dogdisk/shared'
import { useWithdrawDetail } from '../_lib/agent-api'

const STATUS_STEPS = ['pending', 'awaiting_review', 'processing', 'completed'] as const

const STATUS_LABEL: Record<string, string> = {
  pending: '待确认',
  awaiting_review: '待审核',
  approved: '审核通过',
  processing: '处理中',
  completed: '已到账',
  rejected: '已驳回',
  cancelled: '已撤销',
}

export function WithdrawDetailDrawer({
  withdrawId,
  open,
  onClose,
}: {
  withdrawId: string | null
  open: boolean
  onClose: () => void
}) {
  const { data, isLoading, error } = useWithdrawDetail(withdrawId)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} aria-hidden="true" />
      <aside
        data-testid="t0hY8H"
        data-frame-id="t0hY8H"
        className="fixed bottom-0 right-0 top-0 z-50 flex w-[560px] flex-col bg-white shadow-2xl"
        style={{ boxShadow: '-12px 0 32px rgba(0,0,0,0.12)' }}
        role="dialog"
        aria-label="提现详情"
      >
        <header className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-[17px] font-semibold text-gray-900">提现详情</h2>
            {data && (
              <p className="text-[11px] text-gray-400">
                {data.amount_coin.toLocaleString()} COIN · {data.payout_method.toUpperCase()} ·{' '}
                {STATUS_LABEL[data.status] ?? data.status}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="关闭"
          >
            <X size={18} />
          </button>
        </header>

        {isLoading && (
          <div className="flex flex-1 items-center justify-center">
            <Spinner className="text-amber-500" />
          </div>
        )}

        {error && (
          <div className="p-6">
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {error instanceof Error ? error.message : '加载失败'}
            </p>
          </div>
        )}

        {data && (
          <>
            {/* 状态机 */}
            <section className="border-b border-gray-200 bg-emerald-50 px-6 py-4">
              <p className="pb-2 text-xs font-semibold text-emerald-700">状态机</p>
              <div className="flex items-center gap-2">
                {STATUS_STEPS.map((s, idx) => {
                  const reached = STATUS_STEPS.indexOf(data.status as typeof STATUS_STEPS[number]) >= idx
                  return (
                    <div key={s} className="flex flex-1 items-center gap-2">
                      <div className="flex flex-col items-center gap-1">
                        <span
                          className={[
                            'inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold',
                            reached ? 'bg-emerald-500 text-white' : 'bg-gray-200 text-gray-500',
                          ].join(' ')}
                        >
                          {idx + 1}
                        </span>
                        <span className="text-[10px] text-gray-600">{STATUS_LABEL[s] ?? s}</span>
                      </div>
                      {idx < STATUS_STEPS.length - 1 && (
                        <span
                          className={[
                            'h-px flex-1',
                            reached ? 'bg-emerald-500' : 'bg-gray-200',
                          ].join(' ')}
                        />
                      )}
                    </div>
                  )
                })}
              </div>
            </section>

            {/* 基本信息 */}
            <section className="flex flex-col gap-2 border-b border-gray-200 px-6 py-4">
              <h3 className="pb-1 text-sm font-semibold text-gray-900">基本信息</h3>
              <Row label="金额">
                <span className="font-medium">{data.amount_coin.toLocaleString()} COIN</span>
              </Row>
              <Row label="支付方式">{data.payout_method.toUpperCase()}</Row>
              <Row label="收款地址">
                <code className="break-all font-mono text-xs text-gray-700">
                  {data.payout_contact ?? '—'}
                </code>
              </Row>
              <Row label="申请时间">{formatDate(data.created_at)}</Row>
              <Row label="更新时间">{formatDate(data.updated_at)}</Row>
              <Row label="idempotency_key">
                <code className="break-all font-mono text-[11px] text-gray-500">
                  {data.idempotency_key}
                </code>
              </Row>
            </section>

            {/* 拒绝原因(若有)*/}
            {data.reject_reason && (
              <section className="bg-amber-50 px-6 py-4">
                <h3 className="pb-1 text-sm font-semibold text-amber-800">客服备注 / 拒绝原因</h3>
                <p className="text-[13px] text-amber-700">{data.reject_reason}</p>
              </section>
            )}
          </>
        )}
      </aside>
    </>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[13px]">
      <span className="text-gray-500">{label}</span>
      <span className="text-right text-gray-700">{children}</span>
    </div>
  )
}
